import { GroupType } from '../enums/group-type.enum';
import { IEmployee, IEmployeeInBooking } from './employee.model';

export interface Area {
  createdOn: Date;
  floor: Floor;
  id: number;
  isDefault: boolean;
}

export interface Desk {
  angle: number;
  area: Area;
  createdOn: Date;
  id: number;
  xCoordinate: number;
  yCoordinate: number;
  status?: string;
  deskImage?: HTMLImageElement;
  relativeX?: number;
  relativeY?: number;
  hovering?: boolean;
  occupancyPercentage?: number;
  editable?: boolean;
}

export interface Group {
  angle: number;
  createdOn: Date;
  id: number;
  deskType: GroupType;
  xCoordinate: number;
  yCoordinate: number;
  desks: Desk[];
  area?: Area;
}

export interface BookingRecord {
  id: number;
  startDate: Date;
  endDate: Date;
}

export interface ReservationRecord {
  id: number;
  startDate: Date;
  endDate: Date;
}

export interface DeskDetails {
  angle: number;
  areaId?: number;
  id: number;
  deskType: string;
  status: string;
  xCoordinate: number;
  yCoordinate: number;
  employeeAvatarPath?: string;
  editable: boolean;
  bookingRecord?: BookingRecord;
  reservationRecord?: ReservationRecord;
  occupancyPercentage: number;
}

export interface GroupCreate {
  angle: number;
  deskType: GroupType;
  xCoordinate: number;
  yCoordinate: number;
}

export interface GroupCreateRequest {
  areaId?: number;
  desks: GroupCreate[];
  floorId: number;
}

export interface Floor {
  isActive: boolean;
  createdOn: string;
  deleted: boolean;
  floorId: number;
  imageName: string;
  name: string;
  isPrimary: boolean;
}

export interface FloorCreateRequest {
  floorName: string;
  image: Blob;
}

export interface FloorEditRequest {
  isActive: boolean;
  id: number;
  image: Blob;
  name: string;
  isPrimary: boolean;
}

export type GroupImageType = GroupType | 'remove';

export type DeskImageType = 'available' | 'not-available' | 'can-book' | 'defaultAvatar';

export interface DeskUpdateRequest {
  angle: number;
  id: number;
  xCoordinate: number;
  yCoordinate: number;
}

export interface Booking {
  bookingId: number;
  employee?: IEmployeeInBooking;
  guest?: IGuest;
  desk: Desk;
  startDate: Date;
  endDate: Date;
}

export interface IGuest {
  name: string;
  email?: string;
}

export interface IGuestDesk {
  deskId: number;
  name: string;
  email: string;
}

export interface GuestBookingCreate {
  startDate: string;
  endDate: string;
  pattern?: string;
  guests: IGuestDesk[];
}

export interface GuestBookingUpdate {
  startDate: string;
  endDate: string;
  pattern?: string;
  name: string;
  email: string;
}

export interface BookingCreate {
  floorId: string;
  deskId: number;
  employee: number;
  startDate: string;
  endDate: string;
}

export interface BookingUpdate {
  bookingId: number;
  deskId: number;
  employee: number;
  startDate: string;
  endDate: string;
}

export interface Reservation {
  reservationId: number;
  employee: IEmployee;
  desks: number[];
  employeeIds: number[];
  projectIds: number[];
  startDate: string;
  endDate: string;
}

export interface ReservationCreate {
  desks: number[];
  employees: number[];
  projects: number[];
  startDate: string;
  endDate: string;
}

export interface ReservationUpdate {
  id: number;
  areaId?: number;
  desks: number[];
  employees: number[];
  projects: number[];
  startDate: string;
  endDate: string;
}

export interface ReservationResponse {
  reservationId: number;
  deskIds: number[];
  employeeIds: number[];
  projectIds: number[];
  startDate: string;
  endDate: string;
  createdBy: number;
  createdOn: string;
}

export interface DedicationCreate {
  employeeId: number;
  deskId: number;
  startDate: string;
  endDate: string;
  createdBy: number;
}

export interface MeetingCreate {
  employeeId?: number;
  date: string;
  deskId: number;
  startTime: string;
  endTime: string;
}

export interface MeetingResponse {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  employeeId: number;
  employeeName: string;
  editable: boolean;
}

export interface DeskExtended {
  id: number;
  employeeId?: number;
  status: string;
  bookedFrom?: Date;
  bookedTo?: Date;
  image?: any;
  xCoordinate: number;
  yCoordinate: number;
}

export interface Label {
  id: number;
  floorId: number;
  text: string;
  xCoordinate: number;
  yCoordinate: number;
}

export interface LabelCreateRequest {
  floorId: number;
  text: string;
  xCoordinate: number;
  yCoordinate: number;
}

export interface LabelUpdateRequest {
  id: number;
  text: string;
  xCoordinate: number;
  yCoordinate: number;
}

//Validity for booking and group reservations

export enum BookingObjectType {
  RESERVATION = 'RESERVATION',
  BOOKING = 'BOOKING',
}

export interface ConflictingObject {
  objectType: BookingObjectType;
  objectId: number;
  startDate: string;
  endDate: string;
}

export interface BookingValidityRequest {
  deskIds: number[];
  startDate: string;
  endDate: string;
}

export interface BookingValidityResponse {
  hasConflicts: boolean;
  conflictingObjects: ConflictingObject[];
}
